import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Btn, Spinner } from "../ui";

const inputCls =
  "w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300";

export default function Auth() {
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset");
  const [mode, setMode] = useState(
    resetToken ? "reset" : params.get("mode") === "register" ? "register" : "login"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, register, guest } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(email, password, name || undefined);
      else await login(email, password);
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email, origin: window.location.origin });
      setSent(true);
    } catch {
      toast.error("Could not send the reset email. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset", { token: resetToken, new_password: newPassword });
      toast.success("Password updated — sign in with the new one.");
      setParams({});
      setMode("login");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "That reset link didn't work.");
    } finally {
      setBusy(false);
    }
  };

  const asGuest = async () => {
    setBusy(true);
    try {
      await guest();
      navigate("/app");
    } finally {
      setBusy(false);
    }
  };

  const heading =
    mode === "forgot" ? "Forgot your password?" : mode === "reset" ? "Set a new password." : null;

  return (
    <div data-testid="auth-page" className="min-h-screen bg-[#1F1E1B] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="font-serif-display text-3xl tracking-tight text-white block mb-2">
          Boardroom<sup className="font-mono-label text-[10px] ml-1">®</sup>
        </Link>
        <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A] mb-10">
          We do not have chats. We build cases.
        </p>

        {heading && (
          <h1 className="font-serif-display font-light tracking-tight text-4xl text-white mb-8">{heading}</h1>
        )}

        {mode === "forgot" ? (
          sent ? (
            <div data-testid="forgot-sent">
              <p className="text-sm leading-relaxed text-stone-400 mb-8">
                If an account exists for <span className="text-white">{email}</span>, a reset link is on its way.
                It is good for one hour.
              </p>
              <Btn testid="forgot-back-btn" variant="outline" onClick={() => { setMode("login"); setSent(false); }} className="w-full">
                Back to sign in
              </Btn>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-5" data-testid="forgot-form">
              <p className="text-sm leading-relaxed text-stone-400">
                Enter the email on your account and we'll send a reset link.
              </p>
              <input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className={inputCls}
              />
              <Btn testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full">
                {busy ? "Sending…" : "Send reset link"}
              </Btn>
              <button
                type="button"
                data-testid="forgot-cancel-btn"
                onClick={() => setMode("login")}
                className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-stone-400 hover:text-white transition-colors duration-300"
              >
                Back to sign in
              </button>
            </form>
          )
        ) : mode === "reset" ? (
          <form onSubmit={submitReset} className="space-y-5" data-testid="reset-form">
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              className={inputCls}
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              minLength={6}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={inputCls}
            />
            <Btn testid="reset-submit-btn" type="submit" disabled={busy} className="w-full">
              {busy ? "Updating…" : "Update password"}
            </Btn>
          </form>
        ) : (
          <>
            <form onSubmit={submit} className="space-y-5">
              {mode === "register" && (
                <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className={inputCls} />
              )}
              <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
              <div>
                <input data-testid="auth-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
                {mode === "login" && (
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => setMode("forgot")}
                    className="mt-2.5 font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 hover:text-[#B5A58A] transition-colors duration-300"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Btn testid="auth-submit-btn" type="submit" disabled={busy} className="w-full">
                {busy ? "One moment" : mode === "register" ? "Create free account" : "Sign in"}
              </Btn>
            </form>

            <div className="flex items-center gap-4 my-8">
              <span className="flex-1 h-px bg-white/[0.08]" />
              <span className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-stone-500">or</span>
              <span className="flex-1 h-px bg-white/[0.08]" />
            </div>

            <Btn testid="auth-guest-btn" variant="outline" onClick={asGuest} disabled={busy} className="w-full">
              Continue as guest — no signup
            </Btn>

            <button
              data-testid="auth-google-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/app";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="mt-3 w-full flex items-center justify-center gap-3 border border-white/15 rounded-full px-6 py-3 font-mono-label text-[10px] tracking-[0.25em] uppercase text-stone-300 hover:border-[#B5A58A]/60 hover:text-white transition-colors duration-300"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 2.09 14.96 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.87 6.16-4.87z"/>
                <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58l3.68 2.85c2.15-1.99 3.75-4.92 3.75-8.67z"/>
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1L2.18 7.06C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.68-2.85c-1.02.68-2.32 1.09-3.6 1.09-2.86 0-5.29-2.27-6.16-4.87l-3.66 2.84C3.99 20.53 7.7 23 12 23z"/>
              </svg>
              Continue with Google
            </button>

            <p className="mt-4 text-xs text-stone-500 leading-relaxed">
              Guest cases are always public — anonymized into the archive — and expire after 24 hours unless you create an account.
            </p>

            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="mt-8 font-mono-label text-[10px] tracking-[0.2em] uppercase text-stone-400 hover:text-white transition-colors duration-300"
            >
              {mode === "register" ? "Already have an account? Sign in" : "New here? Create a free account"}
            </button>
          </>
        )}
        {busy && <Spinner label="Working…" />}
      </motion.div>
    </div>
  );
}
